import React from 'react';
import { MixTemplate } from '@/types/production.types';
import { getInstrumentById } from '@data/instrumentLibrary';
import { Sliders, Check, Layers } from 'lucide-react';
import { Badge } from '../ui';

interface MixTemplateCardProps {
  template: MixTemplate;
  isSelected?: boolean;
  onClick?: () => void;
}

const MixTemplateCard: React.FC<MixTemplateCardProps> = ({
  template,
  isSelected = false,
  onClick,
}) => {
  const instrumentNames = template.positions
    .map(p => getInstrumentById(p.instrumentId)?.name)
    .filter(Boolean);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative text-left w-full p-4 rounded-2xl border-2 transition-all
        hover:border-accent/50 hover:bg-slate-800/40 active:scale-[0.98]
        ${isSelected
          ? 'border-accent bg-accent/5 shadow-lg shadow-amber-500/10'
          : 'border-slate-800 bg-slate-950/40'
        }
      `}
    >
      {/* Selected check badge */}
      {isSelected && (
        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-accent flex items-center justify-center text-slate-950">
          <Check size={14} strokeWidth={3} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-accent/10 rounded-lg text-accent flex-shrink-0">
          <Sliders size={18} />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-black text-slate-100 uppercase tracking-tight truncate">{template.name}</h4>
        </div>
      </div>

      {/* Description */}
      <p className="text-[11px] text-slate-400 leading-relaxed italic line-clamp-2 mb-3">"{template.description}"</p>

      {/* Genre badges */}
      <div className="flex flex-wrap gap-1 mb-3">
        {template.genre.slice(0, 3).map(g => (
          <Badge key={g} variant="primary-muted" size="xs" pill className="font-bold uppercase">
            {g}
          </Badge>
        ))}
        {template.genre.length > 3 && (
          <Badge variant="default-muted" size="xs" pill>+{template.genre.length - 3}</Badge>
        )}
      </div>

      {/* Instruments */}
      <div className="pt-3 border-t border-slate-800/60">
        <div className="flex items-center gap-1 text-slate-400 mb-1.5">
          <Layers size={10} />
          <span className="text-[11px] font-black uppercase tracking-widest">{template.positions.length} Instruments</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {instrumentNames.slice(0, 5).map(name => (
            <Badge key={name} variant="info-muted" size="xs" pill className="font-bold">
              {name!.split(' ')[0]}
            </Badge>
          ))}
          {instrumentNames.length > 5 && (
            <Badge variant="default-muted" size="xs" pill>+{instrumentNames.length - 5}</Badge>
          )}
        </div>
      </div>
    </button>
  );
};

export default MixTemplateCard;
